export type AiDataPolicyValue = 'not_chosen' | 'investbanq_only' | 'commercial_allowed';

export type AiDataPolicyChoice = Exclude<AiDataPolicyValue, 'not_chosen'>;

export type AiDataPolicyBehaviour = 'internal' | 'commercial';

export interface PolicyActor {
  id: string;
  name: string | null;
}

export interface AiDataPolicy {
  value: AiDataPolicyValue;
  setBy: PolicyActor | null;
  setAt: string | null;
}

export interface AiDataPolicyChange {
  id: string;
  fromValue: AiDataPolicyValue | null;
  toValue: AiDataPolicyChoice | null;
  createdAt: string;
}

export interface AiDataPolicyChangesPage {
  items: AiDataPolicyChange[];
  total: number;
  page: number;
  hasNextPage: boolean;
}

export interface SetAiDataPolicyInput {
  value: AiDataPolicyChoice;
  onlyIfUndecided: boolean;
}

export interface SetAiDataPolicyResult {
  changed: boolean;
  policy: AiDataPolicy;
}

export interface AiDataPolicyWorkspace {
  peopleCount: number | null;
  admins: { names: string[]; total: number } | null;
}

export const AI_DATA_POLICY_SECTION_ID = 'ai-data-policy';

export const AI_DATA_POLICY_CHANGES_PAGE_SIZE = 10;

export interface AiDataPolicyService {
  getPolicy(): Promise<AiDataPolicy>;
  setPolicy(input: SetAiDataPolicyInput): Promise<SetAiDataPolicyResult>;
  listChanges(page: number): Promise<AiDataPolicyChangesPage>;
  getWorkspace(): Promise<AiDataPolicyWorkspace>;
  subscribe(listener: (policy: AiDataPolicy) => void): () => void;
}

export function behaviourOf(value: AiDataPolicyValue): AiDataPolicyBehaviour {
  return value === 'commercial_allowed' ? 'commercial' : 'internal';
}

export function policyStamp(policy: Pick<AiDataPolicy, 'setAt'> | null | undefined): number {
  if (!policy?.setAt) return 0;
  const at = Date.parse(policy.setAt);
  return Number.isNaN(at) ? 0 : at;
}

export function samePolicy(
  a: AiDataPolicy | null | undefined,
  b: AiDataPolicy | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  return a.value === b.value && policyStamp(a) === policyStamp(b);
}
